import React, { useState, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Helmet } from 'react-helmet-async';
import { debounce } from 'lodash';
import FaqSection from '../../components/Faq/Faq';
import TopBusesRoutes from '../../components/TopBusesRoutes/TopBusesRoutes';
import Footer from '../../components/Footer/Footer';
import ExtraDescription from '../../components/ExtraDescription/ExtraDescription';
import BreadCrumb from '../../components/BreadCrumb/BreadCrumb';
import PageHeader from '../../components/PageHeader/PageHeader';
import DataLoaderError from '../../../server/errors/DataLoaderError';
import BusModifySearch from '../../components/BusModifySearch/BusModifySearch';
import { RouteComponentProps } from 'react-router-dom';
import BusAmenities from '../../components/BusAmenities/BusAmenities';
import BusAmenitiesList from '../../components/BusAmenities/BusAmenitiesList';
import BusImages from '../../components/BusImages/BusImages';
import OperatorReviews from '../../components/OperatorReviews/OperatorReviews';
import './OperatorTemplate.css';
import {
  OperatorTemplateDataChecker,
  OperatorTemplateData
} from './OperatorDataTypeChecker';
// import { ThrowReporter } from 'io-ts/lib/ThrowReporter';
import { PathReporter } from 'io-ts/lib/PathReporter';
import { EnvironmentVariables } from '../../types';
import { getAppEnv } from '../../../config/env';
import AppContextProvider from '../../AppContext';
import { trackOperatorTemplateEvent } from '../../utils/omnitureTracking';
import AdsComponent from '../../components/AdsComponent';
import ThingsToKnow from '../../components/ThingsToKnow/ThingsToKnow';
import OperatorGenericInformation from '../../components/OperatorGenericInformation/OperatorGenericInformation';
import TrustMarker from '../../components/TrustMarker/TrustMarker';
import OfferDropdown from '../../components/OfferDropdown/OfferDropdown';
import CompactListV1 from '../../components/CompactList/CompactListV1';
import SchemaInformation, {
  SchemaInformationTypes
} from '../../components/SchemaInformation/SchemaInformation';
import { yieldToMainThread } from '../../utils/scheduler';

const env = getAppEnv();
const { DATA_LOADER_URL } = env.raw as EnvironmentVariables;
const isSSR = typeof window === 'undefined';

type PathParamsType = {
  operatorCode: string;
};
type OperatorTemplateProp = RouteComponentProps<PathParamsType> & {
  data: OperatorTemplateData;
};
type MousePosType = {
  x: number;
  y: number;
};

const runtimeTypChecker = (data: OperatorTemplateData) => {
  const result = OperatorTemplateDataChecker.decode(data);
  if (result._tag !== 'Right') {
    console.log(PathReporter.report(result));
    // ThrowReporter.report(result);
  }
};
const OperatorTemplate = ({ data }: OperatorTemplateProp) => {
  const {
    operatorName,
    noOfBuses,
    noOfRoutes,
    areaServed,
    ownedBy,
    headOffice,
    templateData,
    topRoutesList,
    busTypes,
    busAmenities,
    description,
    additionalInfo,
    offers,
    busReviews,
    busImagesUrl,
    routesList,
    mySafetyHtmls,
    otherOperatorLinks,
    rtcData,
    seoOfferData,
    schemaInfo
  } = data;
  // runtimeTypChecker(data)
  const [collapsableFlag, setcollapsableFlag] = useState(false);
  const [fromCity, setFromCity] = useState('');
  const [toCity, setToCity] = useState('');
  const [fromCode, setFromCode] = useState('');
  const [toCode, setToCode] = useState('');
  const [widget, setWidget] = useState<null | number>(null);

  const _mqMobile = useMediaQuery({
    query: '(max-device-width: 768px)'
  });
  const [isHydrated, setIsHydrated] = useState(false);
  useEffect(() => {
    setIsHydrated(true);
  }, []);
  const isMobileDevice = isHydrated ? _mqMobile : false;

  const [mousePos, setMousePos] = useState({} as MousePosType);
  const [openOffersDropdown, setOpenOffersDropdown] = useState(false);
  const [hasDropdownOpened, setHasDropdownOpened] = useState(false);

  useEffect(() => {
    if (seoOfferData?.length > 0) {
      const handleMouseMove = debounce(event => {
        if (!hasDropdownOpened) {
          setMousePos({ x: event.clientX, y: event.clientY });
        }
      }, 250);

      window.addEventListener('mousemove', handleMouseMove);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, []);

  useEffect(() => {
    if (
      Object.keys(mousePos).length > 0 &&
      mousePos.y <= 20 &&
      !hasDropdownOpened
    ) {
      setOpenOffersDropdown(true);
      setHasDropdownOpened(true);
    }
  }, [mousePos]);

  const onBusClicked = (fromCity, toCity, fromCode, toCode) => {
    yieldToMainThread(() => {
      requestAnimationFrame(() => {
        onFromChange(fromCity, fromCode);
        onToChange(toCity, toCode);
      });
    });
  };

  const onFromChange = (city, code) => {
    yieldToMainThread(() => {
      requestAnimationFrame(() => {
        setFromCity(city);
        setFromCode(code);
      });
    });
    trackOperatorTemplateEvent(`Bus_SEO_Operator_From_Select`);
  };

  const onToChange = (city, code) => {
    yieldToMainThread(() => {
      requestAnimationFrame(() => {
        setToCity(city);
        setToCode(code);
      });
    });
    trackOperatorTemplateEvent(`Bus_SEO_Operator_To_Select`);
  };

  useEffect(() => {
    const seeGuildLinesElement = document.getElementById('SeeGuidelines');
    seeGuildLinesElement &&
      seeGuildLinesElement.addEventListener('click', () => {
        trackOperatorTemplateEvent(
          `Bus_SEO_OperatorTemplate_MySafety_See_GuideLines_Click`
        );
      });

    const learnMoreElement = document.getElementById('LearnMore');
    learnMoreElement &&
      learnMoreElement.addEventListener('click', () => {
        trackOperatorTemplateEvent(
          `Bus_SEO_OperatorTemplate_MySafety_Learn_More_Click`
        );
      });
  }, []);

  if (data.Show404) {
    return (
      <div style={{ textAlign: 'center' }}>
        <h1>404</h1>
        <h2>Sorry, page not found</h2>
      </div>
    );
  }

  const otherOperatorLinksData = {
    mainData: otherOperatorLinks,
    titlePrefix: 'More Bus Operators',
    titleSuffix: ``
  };

  const routesListData = {
    mainData: routesList,
    titlePrefix: `Popular Routes `,
    titleSuffix: `for ${operatorName}`
  };

  const busTypesData = {
    mainData: busTypes,
    titlePrefix: `Bus Types `,
    titleSuffix: `for ${operatorName}`
  };

  const interlinkingBlockData = [
    otherOperatorLinksData,
    routesListData,
    busTypesData
  ];

  return (
    <AppContextProvider
      omniUrlArgs={'source=SEO_Operator_Template'}
      trackTemplateEvent={trackOperatorTemplateEvent}
    >
      {schemaInfo && (
        <SchemaInformation
          schemaInformation={schemaInfo as SchemaInformationTypes}
          templateData={templateData}
        />
      )}

      {/* <div className={`busSeoPage ${collapsableFlag ? '' : 'expanded'}`}> */}
      <div className={`busSeoPage `}>
        {/* <div className={`busSeoPage`}> */}
        <Helmet>
          <title>{templateData.metaTitle}</title>
          <meta name="description" content={templateData.metaDescription} />
          <meta name="keywords" content={templateData.metaKeywords} />
          <link rel="canonical" href={templateData.sampleUrl} />
        </Helmet>
        <div className={`desktopShow makeSticky`}>
          <BusModifySearch
            operatorName={operatorName}
            widget={widget}
            setWidget={setWidget}
            onFromChange={onFromChange}
            onToChange={onToChange}
            fromCity={fromCity}
            toCity={toCity}
            fromCode={fromCode}
            toCode={toCode}
            setcollapsableFlag={setcollapsableFlag}
            collapsableFlag={collapsableFlag}
            isMobileDevice={false}
            demoLink={true}
          />
        </div>
        <div className={`mobileShow makeSticky`}>
          <BusModifySearch
            operatorName={operatorName}
            widget={widget}
            setWidget={setWidget}
            onFromChange={onFromChange}
            onToChange={onToChange}
            fromCity={fromCity}
            toCity={toCity}
            fromCode={fromCode}
            toCode={toCode}
            setcollapsableFlag={setcollapsableFlag}
            collapsableFlag={collapsableFlag}
            isMobileDevice={true}
            demoLink={true}
          />
        </div>

        <div className="container">
          <BreadCrumb breadcrumb={templateData.breadcrumb} type="_Operator" />
        </div>
        {isMobileDevice && rtcData && (
          <section>
            <TrustMarker rtcAcronym={rtcData.acronym} />
          </section>
        )}
        <main className="container">
          <PageHeader title={templateData.h1Title} />
          {templateData.extraInfo && (
            <div className="pageExtraInfo font15 appendTop20 appendBottom15">
              {templateData.extraInfo}
            </div>
          )}
          {
            <AdsComponent
              template={'Operator'}
              placedAt={'top'}
              isMobileDevice={isMobileDevice}
              desktopAdConfig={{
                position: templateData.bannerDesktop
                  ? templateData.bannerDesktop
                  : 'none',
                class: 'desktopShow appendBottom20 adsLayout',
                style: { marginTop: '-10px' }
              }}
              mobileAdConfig={{
                position: templateData.bannerMobile
                  ? templateData.bannerMobile
                  : 'none',
                class: 'mobileShow appendBottom20 adsLayout',
                style: { marginTop: null }
              }}
            />
          }

          {topRoutesList.length > 0 && (
            <>
              <section>
                <div
                  style={{ display: 'none' }} // disabled in SSR HTML
                  className="mobileShow"
                >
                  <p className="lineHeight26 fontSize22 latoLight ">{`Top Bus Routes`}</p>
                  <p className="font16 latoBold lineHeight20 blackText paddingB10">
                    for {operatorName}
                  </p>
                </div>
                <div className="desktopShow font22 latoBlack blackText lineHeight26 appendBottom20">
                  <p>{`Top Bus Routes for ${operatorName}`}</p>
                </div>
                <TopBusesRoutes
                  setcollapsableFlag={setcollapsableFlag}
                  onBusClicked={onBusClicked}
                  noOfListings={templateData.noOfListings}
                  type="Operator"
                  data={topRoutesList}
                  name={operatorName}
                  localOperator={templateData.localBusTemplate}
                />
              </section>
            </>
          )}
          {
            <AdsComponent
              template={'Operator'}
              placedAt={'middle'}
              isMobileDevice={isMobileDevice}
              desktopAdConfig={{
                position: templateData.bannerDesktop
                  ? templateData.bannerDesktop
                  : 'none',
                class: 'desktopShow appendBottom20 adsLayout',
                style: { marginTop: '-10px' }
              }}
              mobileAdConfig={{
                position: templateData.bannerMobile
                  ? templateData.bannerMobile
                  : 'none',
                class: 'mobileShow appendBottom15 adsLayout',
                style: { marginTop: null }
              }}
            />
          }
          {busAmenities && busAmenities.length > 0 && (
            <section className="desktopShow visibilityAuto">
              <p className="font22 latoBold appendBottom20 lineHeight26 blackText">
                Bus Amenities
              </p>
              <BusAmenities busAmenities={busAmenities} />
            </section>
          )}
          {busAmenities && busAmenities.length > 0 && (
            <section className="mobileShow visibilityAuto">
              <p className="font22 latoLight appendBottom22 lineHeight26">
                Bus Amenities
              </p>
              <BusAmenitiesList busAmenities={busAmenities} />
            </section>
          )}
          {busImagesUrl && busImagesUrl.length > 0 && (
            <section className="desktopShow visibilityAuto">
              <p className="busImagesHeading">Bus Photos</p>
              <BusImages busImagesUrl={busImagesUrl} isMobileDevice={false} />
            </section>
          )}
          {busImagesUrl && busImagesUrl.length > 0 && (
            <>
              <section className="mobileShow visibilityAuto">
                <p className="busImagesHeading">Bus Photos</p>
                <BusImages busImagesUrl={busImagesUrl} isMobileDevice={true} />
              </section>
            </>
          )}

          {busReviews && busReviews.length > 0 && (
            <section className="desktopShow visibilityAuto">
              <p className="operatorReviewsHeading">Operator Reviews</p>
              <OperatorReviews busReviews={busReviews} isMobileDevice={false} />
            </section>
          )}
          {busReviews && busReviews.length > 0 && (
            <section className="mobileShow visibilityAuto">
              <p className="operatorReviewsHeading">Operator Reviews</p>
              <OperatorReviews busReviews={busReviews} isMobileDevice={true} />
            </section>
          )}

          <div className={`busSeoWrap__Details makeFlex visibilityAuto`}>
            {interlinkingBlockData.map((item, idx) => {
              const { mainData, titlePrefix, titleSuffix } = item;
              if (!mainData || mainData?.length < 1) {
                return;
              } else {
                return (
                  <CompactListV1
                    blockData={mainData}
                    titlePrefix={titlePrefix}
                    titleSuffix={titleSuffix}
                    key={idx}
                  />
                );
              }
            })}
          </div>

          <section className="visibilityAuto">
            <OperatorGenericInformation
              operatorName={operatorName}
              ownedBy={ownedBy}
              headOffice={headOffice}
              noOfBuses={noOfBuses}
              noOfRoutes={noOfRoutes}
              areaServedBy={areaServed}
              typeOfBuses={busTypes}
            />
          </section>

          <ExtraDescription content={templateData.descriptionInHtml} />
          {/*newsfeed data(can be added via bus-seo-admin UI)*/}
          {additionalInfo && (
            <section>
              <ThingsToKnow title={''} cityName={''} text={additionalInfo} />
            </section>
          )}
          {description && (
            <section>
              <ThingsToKnow
                title={`Things To Know About ${operatorName}`}
                cityName={''}
                text={description}
              />
            </section>
          )}
          {templateData.faq && templateData.faq.length > 0 && (
            <section className="visibilityAuto">
              <FaqSection
                type={'_Operator'}
                FaqTitle={`Frequently Asked Questions Of ${operatorName} Bus Booking`}
                data={templateData.faq}
                pageUrl={templateData.sampleUrl}
                isMobileDevice={isMobileDevice}
              />
            </section>
          )}
        </main>
        {!isMobileDevice &&
          openOffersDropdown &&
          hasDropdownOpened &&
          seoOfferData.length > 0 && (
            <OfferDropdown
              seoOfferData={seoOfferData}
              setOpenOffersDropdown={setOpenOffersDropdown}
            />
          )}
        <Footer template={templateData} />
        <div
          className="blackOverlay"
          onClick={() => {
            setcollapsableFlag(true);
          }}
        ></div>
      </div>
    </AppContextProvider>
  );
};

OperatorTemplate.fetchData = async (_, match, urlObj, gRPCClient) => {
  const {
    params: { operatorId }
  } = match;

  const url = `${DATA_LOADER_URL}/api/operatorTemplateData/${operatorId}?${urlObj.query}`;
  let responseData;
  const response = await fetch(url);
  const textResponse = await response.text();
  try {
    responseData = JSON.parse(textResponse);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new Error('JSON parse exception: ' + textResponse);
  }
  if (responseData.status !== 'SUCCESS') {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new DataLoaderError(
      `Unable to fetch city details of ${operatorId}`,
      responseData.error
    );
  }
  return responseData;
};
export default OperatorTemplate;
